<template>
	<div>
		<p>
			Entrée
			<span :id="'type-info-' + id" class="ml-1 mr-2" style="cursor:pointer;color:#6c757d;">
				<i class="fas fa-info-circle"></i>
			</span>
			<b-select v-model="type" :options="[
				{ value: 'line', text: 'Ligne' },
				{ value: 'file', text: 'Fichier' },
				{ value: 'files', text: 'Fichiers' }
			]" />
			<b-popover :target="'type-info-' + id" triggers="hover focus" placement="right">
				<template #title>Types d'entrée</template>
				<dl class="mb-0" style="font-size:0.88em">
					<dt>Ligne</dt>
					<dd class="mb-2"><code>data</code> = un objet (une ligne). Le code s'exécute une fois par ligne. Retourne l'objet modifié.</dd>
					<dt>Fichier</dt>
					<dd class="mb-2"><code>data</code> = tableau de toutes les lignes d'un fichier. Le code s'exécute une fois par fichier. Retourne le tableau modifié.</dd>
					<dt>Fichiers</dt>
					<dd class="mb-0"><code>data</code> = objet contenant tous les fichiers : <code>{"fichier.csv": [{...}, ...], ...}</code>. Le code s'exécute une seule fois sur l'ensemble. Retourne l'objet entier.</dd>
				</dl>
			</b-popover>
		</p>

		<div class="d-flex align-items-center flex-wrap mb-1">
			<span v-if="!testMode" class="mr-2">Code</span>
			<CodeGenerator
				v-model="code"
				:inline="testMode"
				:class="{ 'flex-grow-1': testMode }"
				:build-request="buildCodegenRequest"
				:validate-code="validateGeneratedCode"
				:load-chat="codegenLoadChat"
				:delete-chat="codegenDeleteChat"
				:columns="columns_data"
				modal-title="Génération de code (JavaScript)"
				prompt-placeholder="Ex: Je veux concaténer les colonnes id et description dans result puis supprimer description."
				ref="codeGenerator"
				@enter-test-mode="enterTestMode">
				<template #options>
					<p v-if="type === 'line'">
						<b-checkbox style="display:inline" v-model="codegen_include_first_line_example" />Envoyer la première ligne comme exemple
					</p>
				</template>
			</CodeGenerator>
			<b-button v-if="!testMode" size="sm" variant="outline-secondary" class="ml-2" @click="toggleDocPanel" :title="showDocPanel ? 'Masquer la documentation' : 'Documentation du script'">
				<i class="fas fa-question-circle"></i>
			</b-button>
			<b-button v-if="!testMode" size="sm" variant="outline-secondary" class="ml-1" v-b-modal="'modal-js-tuto-' + id" title="Tutoriel JavaScript">
				<i class="fas fa-graduation-cap"></i>
			</b-button>
		</div>

		<div v-if="testMode" class="codegen-test-mode-bar">
			<p class="text-muted small mb-2">
				<i class="fas fa-flask"></i> Mode test : navigation et enregistrement verrouillés. Testez avec les boutons "Test" habituels, puis validez ou quittez.
			</p>
			<div class="d-flex justify-content-between">
				<b-button variant="outline-secondary" size="sm" @click="exitTestMode(false)">
					<i class="fas fa-arrow-left"></i> Quitter sans valider
				</b-button>
				<b-button variant="success" size="sm" @click="exitTestMode(true)">
					<i class="fas fa-check"></i> Valider et quitter le mode test
				</b-button>
			</div>
		</div>

		<!-- Modale tutoriel JS -->
		<b-modal :id="'modal-js-tuto-' + id" title="Bases du JavaScript pour les scripts" size="xl" ok-only ok-title="Fermer" scrollable>
			<div class="js-tuto">

				<section class="tuto-section">
					<h5>1. Variables</h5>
					<p>Une variable sert à stocker une valeur pour la réutiliser. On la déclare avec <code>var</code>.</p>
					<pre class="tuto-code">var prenom = "Marie";          // texte (String)
var age = 30;                  // nombre (Number)
var actif = true;              // booléen

// Assigner une nouvelle valeur
prenom = "Jean";</pre>

					<p><strong>Additionner des nombres :</strong></p>
					<pre class="tuto-code">var a = 10;
var b = 5;
var total = a + b;             // 15</pre>

					<p><strong>Concaténer des textes :</strong></p>
					<pre class="tuto-code">var nom = "Dupont";
var prenom = "Marie";
var nomComplet = prenom + " " + nom;   // "Marie Dupont"

// Ou avec un template literal
var nomComplet2 = `${prenom} ${nom}`;  // "Marie Dupont"</pre>

					<p><strong>Dans le script — <code>data</code> est à la fois l'entrée ET la sortie :</strong> c'est toujours <code>data</code> qui est renvoyé à la fin du script. Vous pouvez le modifier en place ou réassigner la variable, mais <strong>le type doit rester identique à celui reçu en entrée</strong>.</p>
					<pre class="tuto-code">// ── Mode ligne ── data est un Object (une seule ligne)
var nomComplet = data.prenom + " " + data.nom;
data.nom_complet = nomComplet;    // modifier en place → pas besoin de réassigner
// data reste un Object ✓

// ── Mode fichier ── data est un Array&lt;Object&gt; (toutes les lignes)
// Modifier en place
for (var i = 0; i &lt; data.length; i++) {
    data[i].prix_ttc = parseFloat(data[i].prix_ht) * 1.2;
}
// Ou réassigner — mais data doit toujours rester un Array&lt;Object&gt;
data = data.filter(function(ligne) { return ligne.actif === "1"; });

// ── Mode fichiers ── data est { "fichier.csv": [lignes...], ... }
// Si vous réassignez, retourner le même type Object contenant des Array&lt;Object&gt;
var fichiers = Object.keys(data);
for (var f = 0; f &lt; fichiers.length; f++) {
    data[fichiers[f]] = data[fichiers[f]].filter(function(l) { return l.actif === "1"; });
    // ⚠️ chaque valeur doit rester un Array&lt;Object&gt;
}</pre>
				</section>

				<section class="tuto-section">
					<h5>2. Parser les nombres et le JSON</h5>
					<p>⚠️ Toutes les valeurs qui arrivent dans <code>data</code> sont des <strong>textes (String)</strong>, même si elles ressemblent à des chiffres. Il faut les convertir avant de faire des calculs.</p>
					<pre class="tuto-code">// Convertir en nombre entier
var quantite = parseInt(data.quantite);       // "12" → 12

// Convertir en nombre décimal
var prix = parseFloat(data.prix);             // "9.99" → 9.99

// Calcul
data.total = quantite * prix;                 // 119.88

// Vérifier si la valeur est un nombre valide
if (!isNaN(prix)) {
    data.total = quantite * prix;
}</pre>

					<p><strong>Parser du JSON :</strong> si une colonne contient du JSON sous forme de texte :</p>
					<pre class="tuto-code">// data.adresse = '{"ville":"Paris","cp":"75001"}'
var adresse = JSON.parse(data.adresse);
data.ville = adresse.ville;                   // "Paris"

// Sérialiser un objet en JSON
data.adresse_json = JSON.stringify(adresse);</pre>
				</section>

				<section class="tuto-section">
					<h5>3. Types de variables : String, Number, Array, Object, null</h5>

					<p><strong>String (texte) :</strong></p>
					<pre class="tuto-code">var s = "Bonjour";
s.length;                      // 7 — nombre de caractères
s.toUpperCase();               // "BONJOUR"
s.toLowerCase();               // "bonjour"</pre>

					<p><strong>Number (nombre) :</strong> entiers et décimaux. ⚠️ Dans les scripts, les valeurs de <code>data</code> arrivent toujours en String — voir section 2 pour les parser.</p>
					<pre class="tuto-code">var n = 42;
var prix = 9.99;

// Opérations
n + 8;                         // 50
prix * 2;                      // 19.98
10 / 3;                        // 3.3333...
10 % 3;                        // 1  (modulo = reste)

// Arrondir
prix.toFixed(2);               // "10.00" (retourne une String !)
Math.round(3.7);               // 4
Math.floor(3.9);               // 3  (arrondi vers le bas)
Math.ceil(3.1);                // 4  (arrondi vers le haut)</pre>

					<p><strong>Array (tableau) :</strong> liste ordonnée de valeurs.</p>
					<pre class="tuto-code">var fruits = ["pomme", "banane", "cerise"];
fruits[0];                     // "pomme" — premier élément (index 0)
fruits.length;                 // 3
fruits.push("kiwi");           // ajoute à la fin
fruits.join(", ");             // "pomme, banane, cerise"</pre>

					<p><strong>Object (objet / JSON) :</strong> ensemble de clés-valeurs. C'est ce qu'est <code>data</code> en mode <em>Ligne</em>.</p>
					<pre class="tuto-code">var personne = { nom: "Dupont", age: 30 };

// Accéder à une propriété — deux syntaxes équivalentes :
personne.nom;                  // "Dupont"  ← à préférer si le nom est simple
personne["nom"];               // "Dupont"</pre>

					<p>👉 Utiliser <code>data["ma colonne"]</code> (avec crochets) quand le nom contient un <strong>espace, un tiret, ou commence par un chiffre</strong>. Sinon <code>data.maColonne</code> fonctionne.</p>
					<pre class="tuto-code">data.nom_complet;              // ✅ pas d'espace
data["nom complet"];           // ✅ avec espace
data["2eme_valeur"];           // ✅ commence par un chiffre
// data.2eme_valeur            // ❌ erreur de syntaxe</pre>

					<p><strong>null et undefined :</strong> absence de valeur.</p>
					<pre class="tuto-code">// null = valeur explicitement vide
data.colonne = null;           // vider une colonne

// undefined = la propriété n'existe pas du tout
data.inexistant;               // undefined

// Tester si une valeur est absente ou vide
if (data.col == null) { ... }          // vrai si null OU undefined
if (data.col === null) { ... }         // vrai uniquement si null
if (!data.col) { ... }                 // vrai si null, undefined, "", 0, false</pre>

					<p><strong>Boolean (vrai / faux) :</strong></p>
					<pre class="tuto-code">var actif = true;
var supprime = false;

// Inverser
!actif;                        // false
!supprime;                     // true

// Combinaisons
true && true;                  // true  (ET — vrai si les deux sont vrais)
true && false;                 // false
true || false;                 // true  (OU — vrai si au moins un est vrai)
false || false;                // false

// En pratique dans les conditions
if (data.actif === "true" || data.actif === "1") {
    // ⚠️ data.actif arrive en String, pas en booléen !
    data.label = "Actif";
}</pre>
				</section>

				<section class="tuto-section">
					<h5>4. Conditions</h5>
					<pre class="tuto-code">if (data.pays === "France") {
    data.tva = "20%";
} else if (data.pays === "Allemagne") {
    data.tva = "19%";
} else {
    data.tva = "inconnue";
}

// Vérifier qu'une valeur n'est pas vide
if (data.nom && data.nom.trim() !== "") {
    data.nom = data.nom.trim();
}</pre>

					<p><strong>Opérateurs de comparaison :</strong></p>
					<pre class="tuto-code">===   égal strict (valeur ET type)  data.pays === "France"
!==   différent strict              data.pays !== "France"
==    égal souple (convertit le type)   42 == "42"  → true
!=    différent souple              42 != "43"  → true

// Préférer === et !== pour éviter les surprises :
0 == false    // true  ← comportement inattendu
0 === false   // false ← correct

&gt;     supérieur                     data.age &gt; 18
&gt;=    supérieur ou égal             data.age &gt;= 18
&lt;     inférieur                     data.age &lt; 18
&lt;=    inférieur ou égal             data.age &lt;= 18

// ⚠️ Ne pas confondre = (assignation) et === (comparaison)
data.pays = "France";      // assigne la valeur "France" à data.pays
data.pays === "France";    // teste si data.pays vaut "France"</pre>

					<p><strong>Opérateurs logiques : <code>!</code>, <code>&amp;&amp;</code>, <code>||</code></strong></p>
					<pre class="tuto-code">!x          // NON — inverse le booléen
x &amp;&amp; y      // ET  — vrai si x ET y sont vrais
x || y      // OU  — vrai si x OU y est vrai (ou les deux)</pre>

					<p><strong>Grouper avec des parenthèses</strong> pour contrôler l'ordre d'évaluation :</p>
					<pre class="tuto-code">// Sans parenthèses : && est évalué avant ||
if (data.pays === "France" || data.pays === "Belgique" &amp;&amp; data.langue === "fr") {
    // lu comme : France  OU  (Belgique ET langue=fr)
}

// Avec parenthèses — comportement explicite
if ((data.pays === "France" || data.pays === "Belgique") &amp;&amp; data.langue === "fr") {
    // lu comme : (France OU Belgique)  ET  langue=fr
}

// Exemple pratique : ville en France OU client premium
if (data.pays === "France" || data.type_client === "premium") {
    data.eligible = "oui";
}

// Exclure une valeur
if (data.statut !== "annule" &amp;&amp; data.statut !== "archive") {
    data.actif = "oui";
}</pre>
				</section>

				<section class="tuto-section">
					<h5>5. Boucles</h5>
					<p>Utiles principalement en mode <em>Fichier</em> ou <em>Fichiers</em> pour parcourir les lignes.</p>
					<pre class="tuto-code">// Parcourir un tableau (mode file : data est un tableau)
for (var i = 0; i &lt; data.length; i++) {
    data[i].nom = data[i].nom.toUpperCase();
}

// Variante moderne
data.forEach(function(ligne) {
    ligne.nom = ligne.nom.toUpperCase();
});

// Transformer chaque élément avec map
data = data.map(function(ligne) {
    ligne.prix_ttc = parseFloat(ligne.prix) * 1.2;
    return ligne;
});

// Filtrer les lignes
data = data.filter(function(ligne) {
    return ligne.pays === "France";
});</pre>
				</section>

				<section class="tuto-section">
					<h5>6. Fonctions utiles sur les textes</h5>
					<pre class="tuto-code">var texte = "  Bonjour le monde  ";

texte.length;                              // 22 — nombre de caractères
texte.trim();                              // "Bonjour le monde" — supprime les espaces
texte.toUpperCase();                       // "  BONJOUR LE MONDE  "
texte.toLowerCase();                       // "  bonjour le monde  "

// Remplacer — replace(chercher, remplacer)
texte.replace("Bonjour", "Salut");         // "  Salut le monde  "

// Remplacer TOUTES les occurrences (regex avec /g)
"a-b-c".replace(/-/g, "_");               // "a_b_c"

// Vérifier si un texte contient une chaîne
texte.includes("monde");                   // true
texte.startsWith("  Bon");                 // true
texte.endsWith("de  ");                    // true

// Découper en tableau
"Paris;Lyon;Bordeaux".split(";");          // ["Paris", "Lyon", "Bordeaux"]

// Extraire une partie
"Bonjour".substring(0, 3);                // "Bon"  (début, fin exclue)</pre>
				</section>

				<section class="tuto-section">
					<h5>Exemples complets</h5>

					<p><strong>Remplacer un mot et stocker dans une nouvelle colonne :</strong></p>
					<pre class="tuto-code">// Mode ligne
data.description_corrigee = data.description.replace("ancien mot", "nouveau mot");
// Retourner data n'est pas nécessaire, il est modifié en place</pre>

					<p><strong>Accéder à une valeur dans un JSON et la mettre dans une colonne :</strong></p>
					<pre class="tuto-code">// data.infos = '{"ville":"Paris","cp":"75001","tags":["A","B"]}'
var infos = JSON.parse(data.infos);
data.ville   = infos.ville;              // "Paris"
data.premier_tag = infos.tags[0];        // "A"</pre>

					<p><strong>Calculer un prix TTC :</strong></p>
					<pre class="tuto-code">data.prix_ttc = (parseFloat(data.prix_ht) * 1.2).toFixed(2);</pre>

					<p><strong>Fusionner deux fichiers en un seul (mode Fichiers) :</strong></p>
					<pre class="tuto-code">// data = { "clients.csv": [{...}, ...], "commandes.csv": [{...}, ...] }
// Objectif : ajouter toutes les lignes de commandes.csv à la suite de clients.csv

var clients   = data["clients.csv"];
var commandes = data["commandes.csv"];

// Fusionner les deux tableaux dans clients.csv
data["clients.csv"] = clients.concat(commandes);

// Supprimer le fichier commandes.csv du résultat
delete data["commandes.csv"];</pre>

					<p><strong>Ajouter une colonne "source" indiquant le fichier d'origine (mode Fichiers) :</strong></p>
					<pre class="tuto-code">// Parcourir chaque fichier et chaque ligne
var nomsFichiers = Object.keys(data);
for (var i = 0; i &lt; nomsFichiers.length; i++) {
    var nom = nomsFichiers[i];
    for (var j = 0; j &lt; data[nom].length; j++) {
        data[nom][j].source = nom;   // ex: "clients.csv"
    }
}</pre>
				</section>

				<section class="tuto-section">
					<h5>8. Fonctions personnalisées</h5>
					<p>Vous pouvez définir vos propres fonctions pour factoriser du code répété.</p>
					<pre class="tuto-code">// Définir une fonction
function nettoyer(texte) {
    if (!texte) return "";
    return texte.trim().toLowerCase();
}

// L'appeler
data.nom = nettoyer(data.nom);
data.prenom = nettoyer(data.prenom);</pre>

					<p>Une fonction peut retourner plusieurs valeurs via un objet :</p>
					<pre class="tuto-code">function decomposer(nomComplet) {
    var parts = nomComplet.split(" ");
    return {
        prenom: parts[0] || "",
        nom: parts[1] || ""
    };
}

var result = decomposer(data.nom_complet);
data.prenom = result.prenom;
data.nom    = result.nom;</pre>

					<p>⚠️ <strong>Pas d'async/await</strong> — le code s'exécute dans un environnement synchrone.</p>
					
				</section>

				<section class="tuto-section">
					<h5>9. Gestion des erreurs et logs</h5>

					<p><strong>try / catch / throw</strong> — intercepter et gérer les erreurs :</p>
					<pre class="tuto-code">try {
    var obj = JSON.parse(data.json_brut);  // peut échouer si JSON invalide
    data.ville = obj.ville;
} catch (err) {
    // err est l'erreur levée par JSON.parse
    data.ville = "";                       // valeur par défaut en cas d'erreur
    console.error("JSON invalide : " + err.message);
}

// throw — lever une erreur explicite (dans une fonction ou directement)
function verifierLigne(d) {
    if (!d.id) {
        throw new Error("La colonne id est vide pour cette ligne");
    }
    return d;
}

// Récupérer l'erreur dans un try/catch appelant
try {
    verifierLigne(data);
    data.statut = "ok";
} catch (err) {
    // err.message contient le texte passé à new Error(...)
    data.statut = "erreur : " + err.message;
}</pre>

					<p><strong>console.log / console.error</strong> :</p>
					<pre class="tuto-code">console.log("valeur de data.prix :", data.prix);   // inspecter une valeur
console.error("quelque chose a mal tourné");        // signaler une erreur</pre>

					<p><strong>response.stop / response.error</strong> — arrêter ou signaler une erreur au niveau du traitement (pas seulement du script) :</p>
					<pre class="tuto-code">// Arrêter l'exécution des étapes suivantes du traitement (après cette étape)
if (!data.id) {
    response.stop = true;
}

// Signaler une erreur (visible dans l'interface). Si l'option "Strict" (⚙ de l'étape)
// est activée, l'erreur arrête aussi les étapes suivantes, comme response.stop
if (!data.email) {
    response.error = "email manquant pour la ligne " + data.id;
}</pre>

					<p>⚠️ <strong>Limitations importantes :</strong></p>
				<ul>
					<li><strong>Jusqu'à 100 erreurs</strong> non interceptées sont remontées dans l'interface, tronquées à 10 000 caractères par ligne. Utilisez <code>try/catch</code> si vous voulez les gérer toutes.</li>
					<li><strong>Les <code>console.log</code> sont affichés</strong> dans la section <em>Logs du script</em> sous les erreurs — limités à 500 lignes × 1 000 caractères.</li>
					<li>En <strong>mode navigateur</strong> (test dans l'éditeur), les logs apparaissent également dans la <strong>console DevTools</strong> du navigateur (F12 → onglet Console).</li>
					<li>En <strong>mode worker</strong> (exécution sur le serveur), les logs apparaissent dans la section <em>Logs du script</em> de l'interface.</li>
				</ul>
				</section>

			</div>
		</b-modal>
		<div v-show="!testMode" class="script-editor-container" :class="{ 'script-editor-with-doc': showDocPanel }">
			<div class="script-ace-wrapper">
				<pre style="width: 100%; height: 100%" :id="'ace_editor-' + id"></pre>
			</div>
			<div v-if="showDocPanel" class="script-doc-panel">
				<div class="d-flex justify-content-between align-items-center mb-2">
					<strong style="font-size:0.9em">Documentation</strong>
					<b-button size="sm" variant="link" class="p-0" @click="showDocPanel = false"><i class="fas fa-times"></i></b-button>
				</div>
				<b-form-input v-model="docFilter" size="sm" placeholder="Filtrer..." class="mb-2" />
				<div v-for="section in filteredDocSections" :key="section.key" class="mb-3">
					<div class="script-doc-section-title">{{ section.label }}</div>
					<div
						v-for="item in section.items"
						:key="item.name"
						class="script-doc-item"
						@click="insertAtCursor(item.snippet)"
						:title="'Insérer : ' + item.snippet">
						<code class="script-doc-item-name">{{ item.name }}</code>
						<small class="text-muted script-doc-item-desc">{{ item.description }}</small>
					</div>
				</div>
			</div>
		</div>

		<p>Paramètres complémentaires
			<b-button
				v-b-modal="'fonction-params-' + id"
				variant="link">
				<i class="fas fa-plus"></i>
			</b-button>

			<b-modal :id="'fonction-params-' + id" ok-only title="Paramètres complémentaires">
				<p><b-checkbox style="display:inline" v-model="display.credentials" />Utliser les clés de sécurité</p>
				<p><b-checkbox style="display:inline" v-model="display.db_requests" />Requêtes à la base de données</p>
				<p><b-checkbox style="display:inline" v-model="display.db_requests_code" />Requêtes à la base de données, avec code</p>
				<p><b-checkbox style="display:inline" v-model="display.lists" />Utiliser des listes</p>

			</b-modal>
		</p>

		<p v-if="display.credentials">
			Utliser les clés de sécurité
			<b-checkbox v-model="credentials" />
		</p>

		<p v-if="display.db_requests">
			Requêtes à la base de données
			<b-button variant="link" @click="addDbRequest()"><i class="fas fa-plus"></i></b-button>
		</p>

		<b-table
			v-if="display.db_requests"
			v-show="db_requests_rows.length > 0"
			striped
			hover
			:items="db_requests_rows"
			:fields="[
				{ key: 'name', label: 'Nom', thStyle: { width: '25%' } },
				{ key: 'request', label: 'Requête', thStyle: { width: '65%' } },
				{ key: 'index', label: '', thStyle: { width: '10%' } }
			]"
		>
			<template v-slot:cell(name)="row">
				<b-input v-model="row.item.name" placeholder="ex: products" />
			</template>
			<template v-slot:cell(request)="row">
				<b-form-textarea
					v-model="row.item.request"
					rows="3"
					max-rows="12"
					placeholder="SELECT ..."
				/>
			</template>
			<template #cell(index)="row">
				<b-button variant="link" @click="db_requests_rows.splice(row.index, 1)"><i class="fa fa-trash"></i></b-button>
			</template>
		</b-table>

		<p v-if="display.db_requests_code">
			Requêtes à la base de données, avec code
			<b-button variant="link" @click="addDbRequestCode()"><i class="fas fa-plus"></i></b-button>
		</p>

		<b-table
			v-if="display.db_requests_code"
			v-show="db_requests_code_rows.length > 0"
			striped
			hover
			:items="db_requests_code_rows"
			:fields="[
				{ key: 'name', label: 'Nom', thStyle: { width: '20%' } },
				{ key: 'request', label: 'Requête', thStyle: { width: '40%' } },
				{ key: 'code', label: 'Code', thStyle: { width: '30%' } },
				{ key: 'index', label: '', thStyle: { width: '10%' } }
			]"
		>
			<template v-slot:cell(name)="row">
				<b-input v-model="row.item.name" placeholder="ex: products" />
			</template>
			<template v-slot:cell(request)="row">
				<b-form-textarea
					v-model="row.item.request"
					rows="3"
					max-rows="12"
					placeholder="SELECT ..."
				/>
			</template>
			<template v-slot:cell(code)="row">
				<b-form-textarea
					v-model="row.item.code"
					rows="3"
					max-rows="12"
					placeholder="// JavaScript"
				/>
			</template>
			<template #cell(index)="row">
				<b-button variant="link" @click="db_requests_code_rows.splice(row.index, 1)"><i class="fa fa-trash"></i></b-button>
			</template>
		</b-table>

		<p v-if="display.lists">
			Listes
			<span :id="'lists-info-' + id" class="ml-1 mr-2" style="cursor:pointer;color:#6c757d;">
				<i class="fas fa-info-circle"></i>
			</span>
			<b-button variant="link" @click="addListRow()"><i class="fas fa-plus"></i></b-button>
			<b-popover :target="'lists-info-' + id" triggers="hover focus" placement="right">
				<template #title>Utiliser des listes</template>
				<p class="mb-1">Comme dans l'étape « Chercher et classer », vous pouvez référencer une liste enregistrée pour l'utiliser dans votre script.</p>
				<p class="mb-0">Dans le code : <code>lists.NOM_VARIABLE</code> est un tableau de groupes de synonymes (le premier mot de chaque groupe est la valeur canonique). Utilisez <code>tools.list_match(lists.NOM_VARIABLE, data.colonne)</code> pour retrouver la valeur canonique correspondant à une valeur.</p>
			</b-popover>
		</p>

		<b-table
			v-if="display.lists"
			v-show="lists_rows.length > 0"
			striped
			hover
			:items="lists_rows"
			:fields="[
				{ key: 'name', label: 'Nom de variable (lists.NOM)', thStyle: { width: '35%' } },
				{ key: 'list', label: 'Liste', thStyle: { width: '55%' } },
				{ key: 'index', label: '', thStyle: { width: '10%' } }
			]"
		>
			<template v-slot:cell(name)="row">
				<b-input v-model="row.item.name" placeholder="ex: villes" />
			</template>
			<template v-slot:cell(list)="row">
				<b-input-group size="sm">
					<b-input v-model="row.item.list" size="sm" placeholder="nom de la liste" />
					<b-input-group-append>
						<b-button size="sm" variant="outline-secondary" @click="open_list_editor(row.index)" title="Gérer cette liste"><i class="fas fa-list"></i></b-button>
					</b-input-group-append>
				</b-input-group>
			</template>
			<template #cell(index)="row">
				<b-button variant="link" @click="lists_rows.splice(row.index, 1)"><i class="fa fa-trash"></i></b-button>
			</template>
		</b-table>

		<b-modal
			:id="'modal_list_editor_' + id"
			size="xl"
			title="Gestion des listes"
			ok-title="Utiliser cette liste"
			cancel-title="Fermer"
			no-close-on-backdrop
			no-close-on-esc
			@ok="use_list"
			@shown="on_list_modal_shown"
		>
			<ElementEditor ref="element_list" _type="list" :minimal="false"></ElementEditor>
		</b-modal>
	</div>
</template>

<script>
	import uniqueId from "lodash.uniqueid";
	import store from "../../store";
	import workers from "../../services/workers.js";
	import treatments from "../../services/treatments.js";
	import * as json2csv from "json2csv";
	import CodeGenerator from "../CodeGenerator.vue";
	import ElementEditor from "../../views/Element.vue";
	import scenario from "../../services/scenario.js";
	import "../../../public/js/ace-builds/src-min-noconflict/ace.js";
	import "../../../public/js/ace-builds/src-min-noconflict/ext-language_tools.js";

	let aceSnippetsRegistered = false;

	export default {
		name: "fonction",
		components: {
			CodeGenerator,
			ElementEditor
		},
		props: {
			treatment_params: {
				default: false
			},
			files: {
				default: () => []
			},
			data: {
				default: () => []
			},
			result_data: {
				default: () => []
			},
			client: {
				default: null
			},
			size: {
				default: 1000
			},
			step_position: {
				default: 0
			},
			// Uid du scénario / de cette étape (Project_v2.vue -> Step.vue), utilisés
			// pour la persistance S3 paresseuse des conversations IA de génération de
			// code (codegen chats, cf CodeGenerator.vue et TODO_CODEGEN_CHAT.md
			// section 4). scenario_uid peut être vide tant que le scénario n'a jamais
			// été enregistré : dans ce cas les chats restent 100% en mémoire.
			scenario_uid: {
				default: ""
			},
			step_uid: {
				default: ""
			}
		},
		watch: {
			data: {
				deep: true,
				immediate: true,
				handler()
				{
					if (this.data && this.data.length)
					{
						this.columns_data = Object.keys(this.data[0]);
					}
				}
			},
			files: {
				deep: true,
				immediate: true,
				handler()
				{
					if (this.files && this.files.length > 0)
					{
						var self = this;
						var reader = new FileReader();

						reader.onload = (function (theFile) {
							return function (e) {
								if (e.target.result)
								{
									var data = self.$papa.parse(e.target.result, { header: true });
									if (data.data && data.data.length > 0)
									{
										self.columns_data = Object.keys(data.data[0]);
									}
								}
							};
						})(this.files[0].file ? this.files[0].file : this.files[0]);

						reader.readAsText(this.files[0].file ? this.files[0].file : this.files[0]);
					}
				}
			}
			,
			db_requests_rows: {
				deep: true,
				handler()
				{
					this.db_requests = this.rowsToDbRequests(this.db_requests_rows);
				}
			},
			db_requests_code_rows: {
				deep: true,
				handler()
				{
					this.db_requests_code = this.rowsToDbRequestsCode(this.db_requests_code_rows);
				}
			},
			lists_rows: {
				deep: true,
				handler()
				{
					this.lists = this.rowsToLists(this.lists_rows);
				}
			}
			,
			code()
			{
				this.syncAceValueFromState();
			},

			showDocPanel() {
				this.$nextTick(() => {
					if (this.ace_editor) this.ace_editor.resize();
				});
			}
		},
		computed: {
			docSections() {
				var sections = [];

				// Variables
				var variables = [];
				if (this.type === 'line') {
					variables.push({ name: 'data', snippet: 'data', description: 'La ligne courante (Object) — ex: data.ma_colonne. C\'est aussi la sortie : assignez vos valeurs sur data (data.col = ...) et elles seront automatiquement renvoyées.' });
					variables.push({ name: 'details', snippet: 'details', description: 'Infos supplémentaires sur le fichier courant' });
					variables.push({ name: 'file', snippet: 'file', description: 'Nom du fichier courant (String)' });
				} else if (this.type === 'file') {
					variables.push({ name: 'condition', snippet: 'condition', description: 'Tableau de booléens — résultat des conditions par ligne' });
					variables.push({ name: 'data', snippet: 'data', description: 'Tableau des lignes du fichier courant (Array<Object>). Entrée ET sortie : si vous réassignez data, retournez un Array<Object>.' });
					variables.push({ name: 'details', snippet: 'details', description: 'Infos sur le fichier (Object)' });
					variables.push({ name: 'file', snippet: 'file', description: 'Nom du fichier courant (String)' });
				} else {
					variables.push({ name: 'condition', snippet: 'condition', description: 'Conditions par fichier et par ligne' });
					variables.push({ name: 'data', snippet: 'data', description: 'Toutes les données : {"fichier.csv": [{col: val}, ...], ...}. Entrée ET sortie : si vous réassignez data, retournez le même type Object<Array<Object>>.' });
					variables.push({ name: 'details', snippet: 'details', description: 'Infos par fichier (Object)' });
				}
				variables.push({ name: 'casse', snippet: 'casse', description: 'Sensibilité à la casse (booléen)' });
				variables.push({ name: 'column', snippet: 'column', description: 'Liste des colonnes configurées' });
				variables.push({ name: 'credentials', snippet: 'credentials', description: 'Clés de sécurité (si activé dans les paramètres)' });
				variables.push({ name: 'data_client', snippet: 'data_client', description: 'Données du client' });
				variables.push({ name: 'data_user', snippet: 'data_user', description: 'Données de l\'utilisateur' });
				variables.push({ name: 'message', snippet: 'message', description: 'Message complet du job (header, uid, ...)' });
				variables.push({ name: 'params', snippet: 'params', description: 'Paramètres de traitement de l\'étape' });
				variables.push({ name: 'treatment_params', snippet: 'treatment_params', description: 'Paramètres dynamiques (traitements)' });
				if ((this.db_requests_rows && this.db_requests_rows.length > 0) || (this.db_requests_code_rows && this.db_requests_code_rows.length > 0)) {
					variables.push({ name: 'db', snippet: 'db', description: 'Résultats des requêtes configurées dans "Requêtes à la base de données" (paramètres complémentaires) — ex: db.NOM (tableau de lignes résultat)' });
					(this.db_requests_rows || []).concat(this.db_requests_code_rows || []).forEach(function(row) {
						if (row.name)
							variables.push({ name: 'db.' + row.name, snippet: 'db.' + row.name, description: 'Résultat (tableau) de la requête "' + row.name + '"' });
					});
				}
				variables.push({ name: 'response', snippet: 'response', description: 'Objet de réponse du traitement — permet de communiquer avec le worker (voir response.stop et response.error ci-dessous)' });
				variables.push({ name: 'response.stop', snippet: 'response.stop = true;', description: 'Mettre à true pour arrêter l\'exécution des étapes suivantes du traitement (celles après cette étape "fonction")' });
				variables.push({ name: 'response.error', snippet: 'response.error = "message d\'erreur";', description: 'Renseigner un message pour signaler une erreur. Si l\'option "Strict" (icône ⚙ en haut de l\'étape) est activée, cela arrête aussi les étapes suivantes, comme response.stop' });
				if (this.lists_rows && this.lists_rows.length > 0) {
					variables.push({ name: 'lists', snippet: 'lists', description: 'Listes configurées ci-dessous — ex: lists.NOM (tableau de groupes de synonymes)' });
					this.lists_rows.forEach(function(row) {
						if (row.name)
							variables.push({ name: 'lists.' + row.name, snippet: 'lists.' + row.name, description: 'Liste "' + (row.list || '?') + '" — tableau de groupes de synonymes, [0] = valeur canonique' });
					});
				}
				sections.push({ key: 'variables', label: 'Variables', items: variables });

				// Librairies
				sections.push({ key: 'libs', label: 'Librairies', items: [
					{ name: '_', snippet: '_', description: 'Underscore.js — _.map, _.filter, _.find, _.groupBy, _.flatten...' },
					{ name: '__', snippet: '__', description: 'Lodash — __.sortBy, __.merge, __.chunk, __.cloneDeep...' },
					{ name: 'Case', snippet: 'Case', description: 'Changer la casse — Case.camel, Case.snake, Case.title, Case.upper...' },
					{ name: 'clone', snippet: 'clone(obj)', description: 'Cloner un objet en profondeur (deep clone)' },
					{ name: 'convert.xml2js()', snippet: "convert.xml2js(data.col, { compact: true })", description: 'Convertir du XML en JSON' },
					{ name: 'crypto', snippet: 'crypto', description: 'Module crypto Node.js — createHash, randomBytes...' },
					{ name: 'CryptoJS.MD5()', snippet: 'CryptoJS.MD5(data.col).toString()', description: 'Hash MD5 — aussi SHA256, AES...' },
					{ name: 'escapeStringRegexp()', snippet: 'escapeStringRegexp(data.col)', description: 'Échapper un texte pour l\'utiliser dans une regex' },
					{ name: 'Fuse', snippet: 'new Fuse(list, { keys: [\'col\'] })', description: 'Recherche floue (fuzzy search) dans un tableau' },
					{ name: 'lngDetector.detect()', snippet: 'lngDetector.detect(data.col)', description: 'Détecter la langue d\'un texte — retourne [["french", 0.9], ...]' },
					{ name: 'moment', snippet: "moment()", description: 'Manipulation de dates — moment().format(\'YYYY-MM-DD\')' },
					{ name: 'removeAccents.remove()', snippet: 'removeAccents.remove(data.col)', description: 'Supprimer les accents d\'un texte' },
					{ name: 'sleep.sleep()', snippet: 'sleep.sleep(1)', description: 'Attendre N secondes (bloquant)' },
					{ name: 'stringSimilarity.compareTwoStrings()', snippet: 'stringSimilarity.compareTwoStrings(a, b)', description: 'Score de similarité entre 2 chaînes (retourne 0 à 1)' },
					{ name: 'sync_request()', snippet: "sync_request('GET', 'https://...').getBody('utf8')", description: 'Requête HTTP synchrone' },
					{ name: 'uuidv4()', snippet: 'uuidv4()', description: 'Générer un UUID v4 unique' },
				]});

				// Fonctions tools
				sections.push({ key: 'tools', label: 'tools.*', items: [
					{ name: 'tools.arrayUnique(array)', snippet: 'tools.arrayUnique(arr)', description: 'Supprimer les doublons d\'un tableau' },
					{ name: 'tools.delete_empty_columns(data)', snippet: 'tools.delete_empty_columns(data)', description: 'Supprimer les colonnes entièrement vides' },
					{ name: 'tools.field_sorter(fields)', snippet: "data.sort(tools.field_sorter(['-colonne']))", description: 'Tri multi-colonnes — préfixe - pour ordre décroissant' },
					{ name: 'tools.match_column(columns, regex, casse)', snippet: "tools.match_column(column, 'regex', false)", description: 'Retourne les noms de colonnes correspondant au regex' },
					{ name: 'tools.mysql(request, data)', snippet: "tools.mysql('SELECT * FROM table WHERE col = ?', [val])", description: 'Requête MySQL synchrone — retourne un tableau de résultats' },
					{ name: 'tools.remove_accent(text)', snippet: 'tools.remove_accent(data.col)', description: 'Supprimer les accents d\'un texte' },
					{ name: 'tools.search(text, regex, casse)', snippet: "tools.search(data.col, 'regex', false)", description: 'Recherche regex — retourne un tableau de correspondances' },
					{ name: 'tools.standardize_json_all(data)', snippet: 'tools.standardize_json_all(data)', description: 'Uniformiser les colonnes entre tous les fichiers' },
					{ name: 'tools.test_condition(data, condition, casse)', snippet: 'tools.test_condition(data, condition, false)', description: 'Tester si une ligne respecte une condition configurée' },
					{ name: 'tools.list_match(list, value)', snippet: 'tools.list_match(lists.NOM, data.col)', description: 'Cherche value dans une liste (lists.NOM) et retourne la valeur canonique du groupe trouvé, sinon null' },
				]});

				return sections;
			},
			filteredDocSections() {
				var filter = (this.docFilter || '').toLowerCase().trim();
				if (!filter) return this.docSections;
				return this.docSections.map(function(section) {
					return Object.assign({}, section, {
						items: section.items.filter(function(item) {
							return item.name.toLowerCase().includes(filter) || item.description.toLowerCase().includes(filter);
						})
					});
				}).filter(function(section) { return section.items.length > 0; });
			}
		},
		data()
		{
			return {
				id: uniqueId(""),

				ace_editor: null,

				showDocPanel: false,
				docFilter: '',

				columns_data: [],

				type: "line",
				code: "",
				credentials: false,

				display: {
					credentials: false,
					db_requests: false,
					db_requests_code: false,
					lists: false
				},

				// Conformément au JSON Schema: ce sont des objets (additionalProperties)
				db_requests: {},
				db_requests_code: {},

				// Représentation UI (lignes) pour éditer les objets ci-dessus
				db_requests_rows: [],
				db_requests_code_rows: [],

				// Listes référencées : [{ name: 'villes', list: 'Villes de France' }, ...]
				lists: [],
				lists_rows: [],
				list_row_index: null,

				codegen_include_first_line_example: false,

				// Mode "test en place" (TODO_CODEGEN_CHAT.md section 10, proposition v4
				// de l'utilisateur) : remplace visuellement l'éditeur Ace par le panneau
				// CodeGenerator (même instance, cf :inline="testMode" ci-dessus), sans
				// toucher à `code` tant que l'utilisateur n'a pas explicitement validé.
				testMode: false
			};
		},
		methods: {
			buildCodegenRequest(prompt, currentCode)
			{
				var mode = (this.type || "line").toLowerCase();
				var modeDesc = "";
				var exampleBlock = "";
				if (mode === "file")
				{
					modeDesc =
`Mode actuel: file
- data est un tableau d'objets (Array<Object>).
- Tu dois transformer chaque ligne/objet.
- La sortie doit rester un tableau d'objets.`;
				}
				else if (mode === "files")
				{
					modeDesc =
`Mode actuel: files
- data est un objet dont chaque propriété est un tableau d'objets.
  Exemple: {"data.csv": [{"id":"1"}], "other.csv": [...]}
- Tu dois appliquer la transformation à chaque tableau.
- La sortie doit rester un objet de tableaux.`;
				}
				else
				{
					modeDesc =
`Mode actuel: line
- data est un objet (Object).
- Tu dois transformer cet objet.
- La sortie doit rester un objet.`;

					if (this.codegen_include_first_line_example)
					{
						var example = null;
						if (Array.isArray(this.data) && this.data.length > 0)
							example = this.data[0];
						else if (this.data && typeof this.data === "object")
							example = this.data;

						if (example)
						{
							var exampleStr = "";
							try {
								exampleStr = JSON.stringify(example, null, 2);
							} catch (e) {
								exampleStr = String(example);
							}
							if (exampleStr && exampleStr.length > 100000)
								exampleStr = exampleStr.substring(0, 100000) + "\n... (tronqué)";

							exampleBlock =
`\n\nExemple d'entrée (première ligne):\n<BEGIN_EXAMPLE>\n${exampleStr}\n<END_EXAMPLE>\n`;
						}
					}
				}

				var libs = [
					"_",
					"__ (lodash)",
					"console",
					"clone",
					"removeAccents",
					"lngDetector",
					"stringSimilarity",
					"escapeStringRegexp",
					"details",
					"Case",
					"message",
					"moment",
					"convert",
					"data_client",
					"data_user",
					"response",
					"uuidv4",
					"crypto",
					"CryptoJS",
					"treatment_params",
					"request",
					"tools.list_match(list, value)",
				];

				var listsBlock = "";
				if (this.lists_rows && this.lists_rows.length > 0)
				{
					var listsNames = this.lists_rows.filter(function(r) { return r.name; }).map(function(r) { return "lists." + r.name + " (liste \"" + (r.list || "?") + "\")"; });
					if (listsNames.length > 0)
					{
						listsBlock =
`

Listes disponibles (tableau de groupes de synonymes, [0] = valeur canonique):
- ${listsNames.join("\n- ")}
Utilise tools.list_match(lists.NOM, valeur) pour retrouver la valeur canonique d'une variante.`;
					}
				}

				var request =
`Tu es un assistant expert en JavaScript pour écrire du code de transformation de données.

Contraintes d'exécution:
- Le code est exécuté dans vm2.
- Interdit: async/await, import/export, require(), accès réseau.
- Interdit: markdown, fences de code autour du code.
- Respecte le contrat de format de réponse donné dans les instructions système (code seul, code + explication, ou question de clarification selon le mode sélectionné).

Variables disponibles:
${modeDesc}
${exampleBlock}${listsBlock}

Règles de données:
- Toutes les valeurs sont des strings: parse les nombres (parseInt/parseFloat) et le JSON (JSON.parse) si nécessaire.
- Le résultat doit être dans data, et data doit garder la même structure externe qu'en entrée.

Librairies/objets utilisables:
- ${libs.join("\n- ")}

Règles:
- Si du code existe déjà, applique la demande EN MODIFIANT ce code et renvoie le code COMPLET final.
- Préfère des opérations explicites et simples.
- N'utilise pas d'asynchrone.

CODE_ACTUEL:
<BEGIN_CODE>
${currentCode || ""}
<END_CODE>

DEMANDE UTILISATEUR:
${prompt}
`;

				return request;
			},
			validateGeneratedCode(code)
			{
				var errors = [];
				var c = (code || "").trim();
				if (!c)
					errors.push("Le code généré est vide.");

				var forbidden = [
					{ re: /\basync\b/, msg: "async est interdit (vm2, pas d'asynchrone)." },
					{ re: /\bawait\b/, msg: "await est interdit (vm2, pas d'asynchrone)." },
					{ re: /\bimport\b/, msg: "import est interdit." },
					{ re: /\bexport\b/, msg: "export est interdit." },
					{ re: /\brequire\s*\(/, msg: "require() est interdit." },
					{ re: /\bfetch\s*\(/, msg: "fetch() est interdit." },
					{ re: /XMLHttpRequest/, msg: "XMLHttpRequest est interdit." },
				];
				for (var i = 0; i < forbidden.length; i++)
				{
					if (forbidden[i].re.test(c))
						errors.push(forbidden[i].msg);
				}

				// Validation syntaxique (approx) côté navigateur
				try {
					// eslint-disable-next-line no-new-func
					new Function("\"use strict\";\n" + c);
				} catch (e) {
					errors.push("Erreur de syntaxe JavaScript: " + (e && e.message ? e.message : e));
				}

				return errors;
			},
			toggleDocPanel() {
				this.showDocPanel = !this.showDocPanel;
			},
			insertAtCursor(snippet) {
				if (!this.ace_editor) return;
				this.ace_editor.focus();
				this.ace_editor.insert(snippet);
			},
			initAce()
			{
				if (this.ace_editor)
					return;
				if (typeof ace === "undefined")
					return;

				if (!aceSnippetsRegistered)
				{
					var snippetManager = ace.require("ace/snippets").snippetManager;
					snippetManager.register(
						[
							{
								tabTrigger: "search",
								name: "search",
								content: "tools.search(data.column, 'regex');",
							},
							{
								tabTrigger: "parse",
								name: "parse",
								content: "data.column = JSON.parse(data.column);",
							},
						],
						"javascript"
					);
					aceSnippetsRegistered = true;
				}

				// trigger extension
				var langTools = ace.require("ace/ext/language_tools");
				langTools.setCompleters([
					langTools.snippetCompleter,
					langTools.textCompleter,
				]);

				var self = this;
				var columnsCompleter = {
					getCompletions: function (editor, session, pos, prefix, callback) {
						var completions = [];
						(self.columns_data || []).forEach(function (columnName) {
							completions.push({
								value: columnName,
								meta: "column",
							});
						});
						callback(null, completions);
					},
				};
				langTools.addCompleter(columnsCompleter);

				var ace_editor = ace.edit("ace_editor-" + this.id);
				this.ace_editor = ace_editor;
				ace_editor.session.setMode("ace/mode/javascript");

				var theme = "chrome";
				if (
					store &&
					store.state &&
					store.state.style &&
					store.state.style.theme &&
					store.state.style.theme.code
				)
					theme = store.state.style.theme.code;
				ace_editor.setTheme("ace/theme/" + theme);

				ace_editor.setOptions({
					enableBasicAutocompletion: true,
					enableSnippets: true,
					enableLiveAutocompletion: true,
				});

				ace_editor.session.on("change", function () {
					self.code = ace_editor.getValue();
				});

				this.syncAceValueFromState(true);
			},
			syncAceValueFromState(force)
			{
				if (!this.ace_editor)
					return;
				var currentValue = this.ace_editor.getValue();
				var nextValue = this.code || "";
				if (force || currentValue !== nextValue)
				{
					this.ace_editor.setValue(nextValue, -1);
				}
			},
			addDbRequest()
			{
				this.db_requests_rows.push({ name: "", request: "" });
			},
			addDbRequestCode()
			{
				this.db_requests_code_rows.push({ name: "", request: "", code: "" });
			},
			rowsToDbRequests(rows)
			{
				var obj = {};
				for (var i = 0; i < rows.length; i++)
				{
					var item = rows[i] || {};
					var name = (item.name || "").trim();
					if (!name)
						continue;
					obj[name] = item.request || "";
				}
				return obj;
			},
			rowsToDbRequestsCode(rows)
			{
				var obj = {};
				for (var i = 0; i < rows.length; i++)
				{
					var item = rows[i] || {};
					var name = (item.name || "").trim();
					if (!name)
						continue;
					obj[name] = {
						request: item.request || "",
						code: item.code || ""
					};
				}
				return obj;
			},
			addListRow()
			{
				this.lists_rows.push({ name: "", list: "" });
			},
			rowsToLists(rows)
			{
				var result = [];
				for (var i = 0; i < rows.length; i++)
				{
					var item = rows[i] || {};
					var name = (item.name || "").trim();
					var list = (item.list || "").trim();
					if (!name || !list)
						continue;
					result.push({ name: name, list: list });
				}
				return result;
			},
			open_list_editor(idx)
			{
				this.list_row_index = idx;
				this.$bvModal.show("modal_list_editor_" + this.id);
			},
			on_list_modal_shown()
			{
				if (!this.$refs.element_list) return;

				var row = this.lists_rows[this.list_row_index];
				var name = row && row.list ? row.list : "";

				if (name)
					this.$refs.element_list.load_name = name;
			},
			async use_list(bvModalEvt)
			{
				if (!this.$refs.element_list)
					return;

				// on empêche la fermeture immédiate : on gère la fermeture nous-même une fois l'enregistrement terminé
				if (bvModalEvt)
					bvModalEvt.preventDefault();

				if (!this.$refs.element_list.name)
				{
					this.$notify({
						group: "info",
						title: "Nom manquant",
						text: "Merci de donner un nom à la liste avant de l'utiliser.",
						type: "error"
					});
					return;
				}

				if (!this.$refs.element_list.version)
					this.$refs.element_list.version = "1";

				if (this.list_row_index !== null && this.lists_rows[this.list_row_index])
				{
					this.lists_rows[this.list_row_index].list = this.$refs.element_list.name;
					if (!this.lists_rows[this.list_row_index].name)
						this.lists_rows[this.list_row_index].name = this.$refs.element_list.name;
				}

				try
				{
					await this.$refs.element_list.save_db();
				}
				catch (err)
				{
					console.error(err);
					this.$notify({
						group: "info",
						title: "Erreur",
						text: "La liste n'a pas pu être enregistrée.",
						type: "error"
					});
					return;
				}

				this.$bvModal.hide("modal_list_editor_" + this.id);
			},
			get_data()
			{
				return this.save();
			},
			save()
			{
				// En mode test (cf enterTestMode/exitTestMode), le code réellement
				// testé par les boutons "Test"/"Test ligne à ligne" de Step.vue (qui
				// passent tous les deux par ce save(), cf component_save()) est le
				// brouillon en cours de discussion dans le générateur — PAS this.code
				// — tant que l'utilisateur n'a pas cliqué "Valider". Si aucun code n'a
				// encore été généré dans cette session de test, on retombe sur le code
				// actuel (comportement identique à avant l'entrée en mode test).
				var codeForSave = this.code;
				if (this.testMode && this.$refs.codeGenerator && this.$refs.codeGenerator.lastGeneratedCode)
					codeForSave = this.$refs.codeGenerator.lastGeneratedCode;

				// type et code sont requis (selon le JSON schema)
				var payload = {
					type: this.type,
					code: codeForSave
				};

				// Tous les autres champs sont optionnels: on ne les inclut que si renseignés
				if (this.credentials)
					payload.credentials = true;
				if (this.db_requests && Object.keys(this.db_requests).length > 0)
					payload.db_requests = this.db_requests;
				if (this.db_requests_code && Object.keys(this.db_requests_code).length > 0)
					payload.db_requests_code = this.db_requests_code;
				if (this.lists && this.lists.length > 0)
					payload.lists = this.lists;
				
				// Sauvegarder l'état de la génération de code via le composant
				// (section 3/4 : conversations multi-tours + chats persistables sur S3,
				// cf CodeGenerator.vue::save() / TODO_CODEGEN_CHAT.md)
				if (this.$refs.codeGenerator)
				{
					const codegenState = this.$refs.codeGenerator.save();
					if (codegenState.chats && codegenState.chats.length > 0)
						payload.codegen_chats = codegenState.chats;
				}

				return payload;
			},
			async load(data)
			{
				if (!data)
				{
					return;
				}

				this.type = data.type || "line";
				this.code = data.code || "";
				this.credentials = !!data.credentials;
				this.display.credentials = this.credentials;

				this.db_requests = data.db_requests || {};
				this.db_requests_rows = [];
				Object.keys(this.db_requests).forEach((name) => {
					this.db_requests_rows.push({ name: name, request: this.db_requests[name] || "" });
				});
				this.display.db_requests = this.db_requests_rows.length > 0;

				this.db_requests_code = data.db_requests_code || {};
				this.db_requests_code_rows = [];
				Object.keys(this.db_requests_code).forEach((name) => {
					var item = this.db_requests_code[name] || {};
					this.db_requests_code_rows.push({
						name: name,
						request: item.request || "",
						code: item.code || ""
					});
				});
				this.display.db_requests_code = this.db_requests_code_rows.length > 0;

				this.lists = Array.isArray(data.lists) ? data.lists : [];
				this.lists_rows = this.lists.map((item) => ({ name: item.name || "", list: item.list || "" }));
				this.display.lists = this.lists_rows.length > 0;

				// Charger l'état de génération de code via le composant
				await this.$nextTick();
				if (this.$refs.codeGenerator)
				{
					this.$refs.codeGenerator.load({
						chats: data.codegen_chats || [],
						// Compat. ascendante : anciens scénarios enregistrés avant la
						// section 3/4 (multi-chat), avec seulement les 3 champs plats.
						prompt: data.codegen_prompt || "",
						codeBeforeGeneration: data.codegen_codeBeforeGeneration || "",
						lastGeneratedCode: data.codegen_lastGeneratedCode || ""
					});
				}

				await this.$nextTick();
				this.syncAceValueFromState();
			},
			// Chargement paresseux du contenu complet d'une conversation IA (cf
			// CodeGenerator.vue prop "load-chat") : uniquement possible si le
			// scénario et cette étape ont déjà un uid stable (donc au moins un
			// enregistrement réussi côté back, cf scenarioCtrl.save).
			async codegenLoadChat(chatRef)
			{
				if (!this.scenario_uid || !this.step_uid)
					return null;
				return await scenario.codegen_chat_load(this.scenario_uid, this.step_uid, chatRef.uid);
			},
			async codegenDeleteChat(chatRef)
			{
				if (!this.scenario_uid || !this.step_uid)
					return;
				await scenario.codegen_chat_delete(this.scenario_uid, this.step_uid, chatRef.uid);
			},
			// Bascule en mode "test en place" (cf CodeGenerator.vue prop `inline`) :
			// signale aussi à Step.vue (puis Project_v2.vue) de verrouiller le reste
			// du scénario, via l'event `codegen-test-mode`.
			enterTestMode()
			{
				this.testMode = true;
				this.$emit('codegen-test-mode', true);
			},
			// Quitte le mode test. apply=true ("Valider") applique le dernier code
			// proposé par le générateur (s'il y en a un et qu'il est valide) via le
			// même mécanisme que le bouton "Appliquer le code généré" existant dans
			// CodeGenerator.vue. apply=false ("Quitter sans valider") ignore le
			// brouillon et restaure simplement l'éditeur Ace sur `this.code`, jamais
			// modifié pendant le mode test.
			exitTestMode(apply)
			{
				var cg = this.$refs.codeGenerator;
				if (apply && cg && cg.lastGeneratedCode)
				{
					if (cg.validation_errors && cg.validation_errors.length > 0)
					{
						this.$notify({
							group: "info",
							type: "error",
							text: "Le code généré contient des erreurs de validation, corrigez-les avant de valider.",
							duration: 4000
						});
						return;
					}
					cg.applyGeneratedCode();
				}
				this.testMode = false;
				this.$emit('codegen-test-mode', false);
				this.$nextTick(() => {
					this.syncAceValueFromState(true);
					if (this.ace_editor)
						this.ace_editor.resize();
				});
			}
		}
		,
		mounted()
		{
			this.initAce();
		},
		beforeDestroy()
		{
			if (this.ace_editor)
			{
				try {
					this.ace_editor.destroy();
					this.ace_editor.container.remove();
				} catch (e) {}
				this.ace_editor = null;
			}
		}
	};
</script>

<style scoped>
.js-tuto {
	font-size: 0.9em;
	line-height: 1.6;
}
.tuto-section {
	margin-bottom: 1.5rem;
	padding-bottom: 1.5rem;
	border-bottom: 1px solid #dee2e6;
}
.tuto-section:last-child {
	border-bottom: none;
}
.tuto-section h5 {
	font-weight: 600;
	margin-bottom: 0.6rem;
	color: #343a40;
}
.tuto-code {
	background: #f8f9fa;
	border: 1px solid #dee2e6;
	border-radius: 4px;
	padding: 10px 14px;
	font-size: 0.85em;
	font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
	white-space: pre;
	overflow-x: auto;
	margin: 0.4rem 0 0.8rem 0;
}
.script-editor-container {
	width: 100%;
	height: 500px;
	display: inline-block;
	position: relative;
}
.script-editor-with-doc {
	display: flex !important;
	gap: 8px;
	align-items: flex-start;
}
.script-ace-wrapper {
	width: 100%;
	height: 100%;
	position: relative;
}
.codegen-test-mode-bar {
	margin-bottom: 0.5rem;
}
.script-editor-with-doc .script-ace-wrapper {
	flex: 1;
	min-width: 0;
	height: 100%;
}
.script-doc-panel {
	width: 300px;
	flex-shrink: 0;
	height: 500px;
	overflow-y: auto;
	border: 1px solid #dee2e6;
	border-radius: 4px;
	padding: 8px;
	font-size: 0.82em;
	background: #fff;
}
.script-doc-section-title {
	font-weight: 600;
	padding: 2px 0 3px 0;
	border-bottom: 1px solid #dee2e6;
	margin-bottom: 3px;
	color: #495057;
	font-size: 0.95em;
}
.script-doc-item {
	padding: 3px 4px;
	cursor: pointer;
	border-radius: 3px;
	display: flex;
	flex-direction: column;
	gap: 1px;
	margin-bottom: 1px;
}
.script-doc-item:hover {
	background-color: #e9ecef;
}
.script-doc-item-name {
	color: #0366d6;
	font-size: 0.88em;
	word-break: break-all;
}
.script-doc-item-desc {
	color: #6c757d;
	font-size: 0.85em;
	line-height: 1.3;
}
</style>
